//App.tsx
//
import {
  HashRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  BrowserRouter,
  useLocation,
  useParams,
} from "react-router-dom"; // Add Outlet
import { UserProvider } from "./contexts/UserContext";
import { useLayoutEffect, useState, useEffect, Suspense, lazy } from "react";
import { GoogleOAuthCallbackHandler } from "./components/GoogleLoginButton";
import DailyLoginTracker from "./components/DailyLoginTracker";

// Lazy-load all pages for code-splitting
const NotFound = lazy(() => import("./pages/NotFound"));
const Home = lazy(() => import("./pages/Home"));
const AllBroshuresPage = lazy(() => import("./pages/AllBroshuresPage"));
const StorePage = lazy(() => import("./pages/Stores/StorePage"));
const BrandPage = lazy(() => import("./pages/Brands/BrandPage"));
const ShoppingList = lazy(() => import("./pages/ShoppingList/ShoppingList"));
const SharedShoppingList = lazy(() => import("./pages/ShoppingList/SharedShoppingList"));
const Profile = lazy(() => import("./pages/Profile/Profile"));
const FavoritePage = lazy(() => import("./pages/Favorites/FavoritePage"));
const CampaignPage = lazy(() => import("./pages/Campaigns/CampaignPage"));
const ProductPage = lazy(() => import("./pages/Campaigns/ProductPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const AllStoresPage = lazy(() => import("./pages/AllStoresPage"));
const AllBrandsPage = lazy(() => import("./pages/AllBrandsPage"));
const AllActiveProductsPage = lazy(() => import("./pages/AllActiveProductsPage"));

// Keep core components static (always needed)
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import MenubarMobile from "./components/MenubarMobile";
import LogoWordmarkLoader from "./components/LogoWordmarkLoader";
import { setupTapGuard } from "./utils/touch";
import { DeviceTypeProvider } from "./contexts/DeviceTypeContext";
import GoogleTracker from "./lib/GoogleTracker";

function ShareRedirect({ includesStoreSegment = false }: { includesStoreSegment?: boolean }) {
  const { "*": sharedPath = "" } = useParams();
  const location = useLocation();
  const targetPath = includesStoreSegment
    ? `/store/${sharedPath}`
    : `/store/${sharedPath}`;

  return (
    <Navigate
      replace
      to={{
        pathname: targetPath,
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

function StoreBrochureRedirect() {
  const { storeAlias } = useParams();
  const location = useLocation();

  return (
    <Navigate
      replace
      to={{
        pathname: `/store/${storeAlias}`,
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

function LegacyCampaignRedirect() {
  const { storeAlias, campaignId, page, product } = useParams();
  const location = useLocation();

  if (!storeAlias || !campaignId || !page) {
    return <Navigate replace to="/" />;
  }

  return (
    <Navigate
      replace
      to={{
        pathname: `/store/${storeAlias}/katalozi/broshura/${campaignId}/${page}${
          product ? `/${product}` : ""
        }`,
        search: location.search,
        hash: location.hash,
      }}
    />
  );
}

function AppContent() {
  const [isMobile, setIsMobile] = useState(false);
  const [isBootLoading, setIsBootLoading] = useState(
    () => document.readyState !== "complete",
  );

  useEffect(() => {
    if (document.readyState === "complete") {
      setIsBootLoading(false);
      return;
    }

    const handleWindowLoad = () => setIsBootLoading(false);
    window.addEventListener("load", handleWindowLoad, { once: true });

    return () => window.removeEventListener("load", handleWindowLoad);
  }, []);

  useEffect(() => {
    const resetAds = () => {
      if (window.adsbygoogle) {
        (window.adsbygoogle as any).loaded = false;
      }
    };

    window.addEventListener("popstate", resetAds);
    return () => window.removeEventListener("popstate", resetAds);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useLayoutEffect(() => {
    // ✅ Globally disable browser scroll restoration on app mount
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useLayoutEffect(() => {
    // ✅ Globally disable browser scroll restoration on app mount
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    // ✅ Call tap guard once on mount
    setupTapGuard();
  }, []);

  // useEffect(() => {
  //   const path = window.location.pathname;
  //   const hash = window.location.hash;

  //   if (path !== "/" && !hash) {
  //     // Absolute redirect to the hash version of the current path
  //     const origin = window.location.origin; // e.g. "http://localhost:3001"
  //     const newUrl = `${origin}#${path}`;

  //     window.location.replace(newUrl);
  //   }
  // }, []);


    useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    const origin = window.location.origin;

// if (path.includes("/all/promo")) {
//   window.location.replace(origin + "/");
// }

// 🟡 2. Normal hash routing
// else if (path !== "/" && !hash) {
//   const newUrl = `${origin}#${path}`;
//   window.location.replace(newUrl);
// }
  }, []);


  return (
    <div className="app-wrapper">
      <LogoWordmarkLoader visible={isBootLoading} />
      <DeviceTypeProvider value={isMobile ? "mobile" : "desktop"}>
        <BrowserRouter>
          {/* <BrowserRouter> */}
          <GoogleTracker />
          <GoogleOAuthCallbackHandler />
          <DailyLoginTracker />
          <Navbar />
          <ScrollToTop /> {/* ← render here once */}
          <Suspense fallback={<LogoWordmarkLoader visible={true} />}>
            <Routes>
              {/* ← WRAP ALL PAGES IN LAYOUT */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} /> {/* / */}
                <Route path="store/:storeAlias" element={<StorePage />} />{" "}
                <Route
                  path="store/:storeAlias/broshura"
                  element={<StoreBrochureRedirect />}
                />
                <Route
                  path="store/:storeAlias/broshura/1"
                  element={<StoreBrochureRedirect />}
                />
                <Route
                  path="share/store/*"
                  element={<ShareRedirect includesStoreSegment />}
                />{" "}
                <Route path="share/*" element={<ShareRedirect />} />{" "}
                <Route path="brand/:brandAlias" element={<BrandPage />} />{" "}
                <Route
                  path="store/:storeAlias/katalozi/broshura/:campaignId"
                  element={<Navigate to="1" replace />}
                />{" "}
                <Route
                  path="store/:storeAlias/katalozi/broshura/:campaignId/:page"
                  element={<CampaignPage />}
                />{" "}
                <Route
                  path="store/:storeAlias/katalozi/broshura/:campaignId/:page/:product"
                  element={<ProductPage />}
                />{" "}
                <Route
                  path="store/:storeAlias/:campaignId/:page/:product"
                  element={<LegacyCampaignRedirect />}
                />
                <Route
                  path="store/:storeAlias/:campaignId/:page"
                  element={<LegacyCampaignRedirect />}
                />
                              {/* /stores/abc */}
                <Route path="shoppinglist" element={<ShoppingList />} />
                <Route path="search/:searchTerm" element={<SearchPage />} />
                <Route
                  path="shoppinglist/:sharedShoppingListId"
                  element={<SharedShoppingList />}
                />
                <Route path="profile/*" element={<Profile />} />
                <Route
    path="/profile/confirm-password-reset/:token"
    element={<Profile />}
  />
                <Route path="favorite" element={<FavoritePage />} />
                <Route path="all/katalog" element={<AllBroshuresPage />} />
                <Route path="all/katalozi" element={<AllBroshuresPage />} />
                <Route path="all/broshura" element={<AllBroshuresPage />} />
                <Route path="all/broshuri" element={<AllBroshuresPage />} />
                <Route path="all/promo" element={<AllBroshuresPage />} />
                <Route path="all/stores" element={<AllStoresPage />} />
                <Route path="all/brands" element={<AllBrandsPage />} />
                <Route path="all/products" element={<AllActiveProductsPage />} />
              </Route>

              {/* NotFound outside layout if needed */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          {isMobile && <MenubarMobile />}
        </BrowserRouter>
        {/* </BrowserRouter> */}
      </DeviceTypeProvider>
    </div>
  );
}

export default function App() {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
}
